import CountryHometownQuestion from 'candidate/components/CountryHometownQuestion'
import DocumentUploadQuestion from 'candidate/components/DocumentUploadQuestion'
import DropdownQuestion from 'candidate/components/DropdownQuestion'
import GraduateDegreesQuestion from 'candidate/components/GraduateDegreesQuestion'
import IntakeCard from 'candidate/components/IntakeCard'
import MultipleChoiceQuestion from 'candidate/components/MultipleChoiceQuestion'
import MultipleResponseQuestion from 'candidate/components/MultipleResponseQuestion'
import StandardizedTestScoreQuestion from 'candidate/components/StandardizedTestScoresQuestion'
import UndergradCollegeQuestion from 'candidate/components/UndergradCollegeQuestion'
import React from 'react'
import NextButton from 'shared/components/NextButton'
import AnalystsQuestion from '../AnalystsQuestion'
import DatePickerQuestion from '../DatePickerQuestion'
import DropdownMultiSelectQuestion from '../DropdownMultiSelectQuestion'
import EmploymentQuestion, { EmploymentAnswer } from '../EmploymentQuestion'
import InternshipQuestion from '../InternshipQuestion'
import PriorEmploymentQuestion from '../PriorEmploymentQuestion'
import ReferenceQuestion from '../ReferenceQuestion'
import StafferQuestion from '../StafferQuestion'

interface IntakeQuestion {
    id: string
    internalName: string
    questionType: string
    questionData: any
    displayName: string
}

interface QuestionProps {
    activeQuestionIndex: number
    intakeName: string
    answers: Record<any, any>[]
    question: IntakeQuestion
    updateAnswers: React.Dispatch<React.SetStateAction<Record<any, any>>>
    existingEmploymentAnswer?: EmploymentAnswer
    existingWorkExperienceId?: string
    skipCallback?: React.Dispatch<React.SetStateAction<string>>
    skipText?: string
}

const Question: React.FC<QuestionProps> = ({
    activeQuestionIndex,
    intakeName,
    answers,
    question: { internalName, questionData, questionType, displayName },
    updateAnswers,
    existingEmploymentAnswer,
    existingWorkExperienceId,
    skipCallback,
    skipText,
}: QuestionProps) => {
    window.scrollTo(0, 0)

    switch (questionType) {
        case 'analysts':
            return (
                <AnalystsQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'country_hometown':
            return (
                <CountryHometownQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    prompt={questionData.prompt}
                    callback={updateAnswers}
                    countryChoices={questionData.countryChoices}
                    provinceChoices={questionData.provinceChoices}
                    stateChoices={questionData.stateChoices}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'document_upload':
            return (
                <DocumentUploadQuestion
                    prompt={questionData.prompt}
                    subtext={questionData.subtext}
                    callback={updateAnswers}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'date_picker':
            return (
                <DatePickerQuestion
                    key={internalName}
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'dropdown':
            return (
                <DropdownQuestion
                    key={internalName}
                    internalName={internalName}
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    choices={questionData.choices}
                    callback={updateAnswers}
                    prompt={questionData.prompt}
                    displayName={displayName}
                />
            )
        case 'dropdown_multiselect':
            return (
                <DropdownMultiSelectQuestion
                    key={internalName}
                    internalName={internalName}
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    choices={questionData.choices}
                    callback={updateAnswers}
                    prompt={questionData.prompt}
                    subtext={questionData.subtext}
                    displayName={displayName}
                    limit={questionData.limit}
                    label={questionData.label}
                    repeat={questionData.repeat}
                />
            )
        case 'employment': {
            const employmentCallback = (answer: Record<any, any>) => {
                if (existingWorkExperienceId) {
                    updateAnswers({ ...answer, existingWorkExperienceId })
                } else {
                    updateAnswers(answer)
                }
            }
            return (
                <EmploymentQuestion
                    answer={
                        answers[activeQuestionIndex]?.answerData?.response ??
                        existingEmploymentAnswer
                    }
                    callback={employmentCallback}
                    workTitles={questionData.workTitles}
                    industryChoices={questionData.industryChoices}
                    sectorChoices={questionData.sectorChoices}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        }
        case 'graduate_degrees':
            return (
                <GraduateDegreesQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    collegeChoices={questionData.collegeChoices}
                    mbaCollegeChoices={questionData.mbaCollegeChoices}
                    degreeChoices={questionData.degreeChoices}
                    gpaChoices={questionData.gpaChoices}
                    gpaScales={questionData.gpaScaleChoices}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'internship':
            return (
                <InternshipQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    industryChoices={questionData.industryChoices}
                    sectorChoices={questionData.sectorChoices}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'multiple_choice':
            return (
                <MultipleChoiceQuestion
                    key={internalName}
                    internalName={internalName}
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    prompt={questionData.prompt}
                    callback={updateAnswers}
                    choices={questionData.choices}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'multiple_response':
            return (
                <MultipleResponseQuestion
                    key={internalName}
                    internalName={internalName}
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    prompt={questionData.prompt}
                    subtext={questionData.subtext}
                    callback={updateAnswers}
                    choices={questionData.multiResponseChoices}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'prior_employment':
            return (
                <PriorEmploymentQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    workTitles={questionData.workTitles}
                    industryChoices={questionData.industryChoices}
                    sectorChoices={questionData.sectorChoices}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'reference':
            return (
                <ReferenceQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    workTitles={questionData.workTitles}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'staffer':
            return (
                <StafferQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    workTitles={questionData.workTitles}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'standardized_test_scores':
            return (
                <StandardizedTestScoreQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        case 'undergraduate_college':
            return (
                <UndergradCollegeQuestion
                    answer={answers[activeQuestionIndex]?.answerData?.response}
                    callback={updateAnswers}
                    collegeChoices={questionData.collegeChoices}
                    majorChoices={questionData.majorChoices}
                    gpaChoices={questionData.gpaChoices}
                    gpaScales={questionData.gpaScaleChoices}
                    prompt={questionData.prompt}
                    skipCallback={skipCallback}
                    skipText={skipText}
                />
            )
        default:
            return (
                <IntakeCard>
                    <h1>Unsupported Question Type: {questionType}</h1>
                    <NextButton
                        disabled={false}
                        onClickHandler={() => updateAnswers({})}
                    />
                </IntakeCard>
            )
    }
}

export default Question
